import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Server,
  AlertCircle,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Plug,
  ChevronDown,
  ChevronUp,
  Wrench,
  Link,
  Link2Off,
  ShieldCheck,
} from 'lucide-react';
import {
  apiService,
  McpServerConfig,
  McpDiscoveredTool,
  McpOAuthStatus,
  CreateMcpServerRequest,
  UpdateMcpServerRequest,
  McpSyncResult,
  McpTransportType,
  McpServerStatus,
} from '../../services/api';
import { toast } from 'sonner';

interface FormData {
  name: string;
  description: string;
  serverUrl: string;
  transportType: McpTransportType;
  isActive: boolean;
}

interface OAuthStatusMap {
  [agentToolId: string]: McpOAuthStatus;
}

const DEFAULT_FORM: FormData = {
  name: '',
  description: '',
  serverUrl: '',
  transportType: 'sse',
  isActive: true,
};

function statusBadge(status: McpServerStatus) {
  switch (status) {
    case 'active':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </Badge>
      );
    case 'error':
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          <XCircle className="w-3 h-3 mr-1" />
          Error
        </Badge>
      );
    case 'connecting':
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Connecting
        </Badge>
      );
    default:
      return (
        <Badge className="bg-gray-100 text-gray-600 border-gray-200">
          <Clock className="w-3 h-3 mr-1" />
          Inactive
        </Badge>
      );
  }
}

interface DiscoveredToolsListProps {
  tools: McpDiscoveredTool[];
  onOAuthClick: (tool: McpDiscoveredTool) => void;
}

function DiscoveredToolsList({ tools, onOAuthClick }: DiscoveredToolsListProps) {
  if (tools.length === 0) {
    return (
      <p className="text-xs text-gray-400 italic px-1">No tools available.</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-1.5 px-2 font-semibold text-gray-600 w-1/4">Name</th>
            <th className="text-left py-1.5 px-2 font-semibold text-gray-600">Description</th>
            <th className="text-right py-1.5 px-2 font-semibold text-gray-600 w-20">Params</th>
            <th className="text-right py-1.5 px-2 font-semibold text-gray-600 w-24">OAuth</th>
          </tr>
        </thead>
        <tbody>
          {tools.map((tool) => {
            const paramCount = Object.keys(tool.inputSchema?.properties ?? {}).length;
            const requiredCount = tool.inputSchema?.required?.length ?? 0;
            return (
              <tr key={tool.name} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                <td className="py-1.5 px-2 font-mono font-medium text-blue-700 align-top">
                  {tool.name}
                </td>
                <td className="py-1.5 px-2 text-gray-600 align-top">
                  {tool.description || <span className="text-gray-400 italic">No description</span>}
                </td>
                <td className="py-1.5 px-2 text-right text-gray-500 align-top whitespace-nowrap">
                  {paramCount} ({requiredCount} req.)
                </td>
                <td className="py-1.5 px-2 text-right align-top">
                  <button
                    onClick={() => onOAuthClick(tool)}
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                    title="Manage OAuth for this tool"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span>OAuth</span>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OAuthStatusBadge({ status }: { status: McpOAuthStatus | null | undefined }) {
  if (!status) return null;

  if (status.requiresReauth) {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
        <AlertCircle className="w-3 h-3 mr-1" />
        Re-auth Required
      </Badge>
    );
  }
  if (status.connected) {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200">
        <ShieldCheck className="w-3 h-3 mr-1" />
        OAuth Connected
      </Badge>
    );
  }
  return (
    <Badge className="bg-gray-100 text-gray-600 border-gray-200">
      <Link2Off className="w-3 h-3 mr-1" />
      Not Connected
    </Badge>
  );
}

interface OAuthManagerPanelProps {
  agentId: string;
  agentToolId: string;
  onStatusChange?: (status: McpOAuthStatus) => void;
}

function OAuthManagerPanel({ agentId, agentToolId, onStatusChange }: OAuthManagerPanelProps) {
  const [status, setStatus] = useState<McpOAuthStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [popupRef, setPopupRef] = useState<Window | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const data = await apiService.getMcpOAuthStatus(agentId, agentToolId);
      setStatus(data);
      onStatusChange?.(data);
    } catch {
      // Status fetch failures are non-critical; show nothing
    } finally {
      setLoadingStatus(false);
    }
  }, [agentId, agentToolId, onStatusChange]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<{ type: string; agentToolId?: string }>) => {
      if (
        event.data?.type === 'oauth_callback_success' &&
        event.data?.agentToolId === agentToolId
      ) {
        popupRef?.close();
        setPopupRef(null);
        toast.success('OAuth connection established');
        fetchStatus();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [agentToolId, popupRef, fetchStatus]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const { authorizationUrl } = await apiService.initiateMcpOAuth(agentId, agentToolId);
      const popup = window.open(
        authorizationUrl,
        'oauth_popup',
        'width=600,height=700,scrollbars=yes,resizable=yes',
      );
      if (!popup) {
        toast.error('Pop-up blocked. Please allow pop-ups for this site.');
        return;
      }
      setPopupRef(popup);
      toast.info('Complete authorization in the opened window');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to initiate OAuth';
      toast.error(message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await apiService.disconnectMcpOAuth(agentId, agentToolId);
      const disconnected: McpOAuthStatus = { connected: false };
      setStatus(disconnected);
      onStatusChange?.(disconnected);
      toast.success('OAuth connection removed');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to disconnect OAuth';
      toast.error(message);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loadingStatus) {
    return <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />;
  }

  return (
    <div className="flex items-center gap-2">
      <OAuthStatusBadge status={status} />
      {status?.connected && !status.requiresReauth ? (
        <Button
          variant="outline"
          size="sm"
          onClick={handleDisconnect}
          disabled={disconnecting}
          className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
        >
          {disconnecting ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Link2Off className="w-3 h-3 mr-1" />
          )}
          Disconnect
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleConnect}
          disabled={connecting}
          className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
        >
          {connecting ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Link className="w-3 h-3 mr-1" />
          )}
          {status?.requiresReauth ? 'Re-authorize' : 'Connect OAuth'}
        </Button>
      )}
    </div>
  );
}

interface OAuthAssignmentDialogProps {
  tool: McpDiscoveredTool;
  open: boolean;
  onClose: () => void;
  oauthStatusMap: OAuthStatusMap;
  onStatusChange: (agentToolId: string, status: McpOAuthStatus) => void;
}

function OAuthAssignmentDialog({
  tool,
  open,
  onClose,
  oauthStatusMap,
  onStatusChange,
}: OAuthAssignmentDialogProps) {
  const [agentId, setAgentId] = useState('');
  const [agentToolId, setAgentToolId] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!agentId.trim() || !agentToolId.trim()) {
      toast.error('Agent ID and Agent Tool ID are required');
      return;
    }
    setSubmitted(true);
  };

  const handleClose = () => {
    setAgentId('');
    setAgentToolId('');
    setSubmitted(false);
    onClose();
  };

  const currentStatus = submitted ? oauthStatusMap[agentToolId.trim()] : undefined;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            OAuth Connection
          </DialogTitle>
          <DialogDescription>
            Manage OAuth authentication for{' '}
            <span className="font-mono font-medium text-gray-800">{tool.name}</span>
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="oauth-agent-id">Agent ID</Label>
              <Input
                id="oauth-agent-id"
                placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
              />
              <p className="text-xs text-gray-400">The UUID of the agent this tool is assigned to</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oauth-agent-tool-id">Agent Tool ID</Label>
              <Input
                id="oauth-agent-tool-id"
                placeholder="e.g. 550e8400-e29b-41d4-a716-446655440001"
                value={agentToolId}
                onChange={(e) => setAgentToolId(e.target.value)}
              />
              <p className="text-xs text-gray-400">The UUID of the agent-tool assignment record</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSubmit}>Continue</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Agent ID</span>
                <span className="font-mono text-gray-700 truncate max-w-[220px]">{agentId.trim()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Agent Tool ID</span>
                <span className="font-mono text-gray-700 truncate max-w-[220px]">{agentToolId.trim()}</span>
              </div>
              {currentStatus?.connectedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Connected at</span>
                  <span className="text-gray-700">{new Date(currentStatus.connectedAt).toLocaleString()}</span>
                </div>
              )}
              {currentStatus?.scopesGranted && currentStatus.scopesGranted.length > 0 && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Scopes</span>
                  <span className="text-gray-700 text-right">{currentStatus.scopesGranted.join(', ')}</span>
                </div>
              )}
            </div>
            <OAuthManagerPanel
              agentId={agentId.trim()}
              agentToolId={agentToolId.trim()}
              onStatusChange={(status) => onStatusChange(agentToolId.trim(), status)}
            />
            <div className="flex justify-between pt-2">
              <Button variant="outline" size="sm" onClick={() => setSubmitted(false)}>
                Back
              </Button>
              <Button variant="outline" onClick={handleClose}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function McpServersPage() {
  const [servers, setServers] = useState<McpServerConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | McpServerStatus>('all');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<McpServerConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncResults, setSyncResults] = useState<Record<string, McpSyncResult>>({});
  const [expandedSyncId, setExpandedSyncId] = useState<string | null>(null);
  const [expandedToolsId, setExpandedToolsId] = useState<string | null>(null);

  const [oauthDialogTool, setOauthDialogTool] = useState<McpDiscoveredTool | null>(null);
  const [oauthStatusMap, setOauthStatusMap] = useState<OAuthStatusMap>({});

  const handleOAuthStatusChange = useCallback((agentToolId: string, status: McpOAuthStatus) => {
    setOauthStatusMap((prev) => ({ ...prev, [agentToolId]: status }));
  }, []);

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getMcpServers();
      setServers(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load MCP servers';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingServer(null);
    setFormData(DEFAULT_FORM);
    setIsDialogOpen(true);
  };

  const openEditDialog = (server: McpServerConfig) => {
    setEditingServer(server);
    setFormData({
      name: server.name,
      description: server.description ?? '',
      serverUrl: server.serverUrl,
      transportType: server.transportType,
      isActive: server.isActive,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.serverUrl.trim()) {
      toast.error('Name and Server URL are required');
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      serverUrl: formData.serverUrl.trim(),
      transportType: formData.transportType,
      isActive: formData.isActive,
    };

    try {
      setSaving(true);
      if (editingServer) {
        const updated = await apiService.updateMcpServer(editingServer.id, payload as UpdateMcpServerRequest);
        setServers((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        toast.success('MCP server updated successfully');
      } else {
        const created = await apiService.createMcpServer(payload as CreateMcpServerRequest);
        setServers((prev) => [...prev, created]);
        toast.success('MCP server created successfully');
      }
      setIsDialogOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save MCP server';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      setDeletingId(deleteTargetId);
      await apiService.deleteMcpServer(deleteTargetId);
      setServers((prev) => prev.filter((s) => s.id !== deleteTargetId));
      toast.success('MCP server deleted');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete MCP server';
      toast.error(message);
    } finally {
      setDeletingId(null);
      setDeleteTargetId(null);
    }
  };

  const handleSync = async (server: McpServerConfig) => {
    try {
      setSyncingId(server.id);
      const result = await apiService.syncMcpServer(server.id);
      setSyncResults((prev) => ({ ...prev, [server.id]: result }));
      setExpandedSyncId(server.id);
      const errorSuffix = result.errors.length > 0 ? `, ${result.errors.length} error(s)` : '';
      toast.success(
        `Sync complete: ${result.toolsDiscovered} tool(s) discovered${errorSuffix}`,
      );
      // Reload to get updated discoveredToolsCount and status
      await loadServers();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      toast.error(message);
    } finally {
      setSyncingId(null);
    }
  };

  const filteredServers = servers.filter((s) => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.description ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.serverUrl.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Plug className="w-6 h-6 text-blue-600" />
            MCP Servers
          </h1>
          <p className="text-gray-500 mt-1">
            Manage Model Context Protocol server catalog and discover tools
          </p>
        </div>
        <Button onClick={openCreateDialog} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add MCP Server
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={loadServers}>
            Retry
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search servers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(val) => setStatusFilter(val as 'all' | McpServerStatus)}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="connecting">Connecting</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={loadServers}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {(
          [
            { label: 'Total', count: servers.length, color: 'text-gray-900' },
            { label: 'Active', count: servers.filter((s) => s.status === 'active').length, color: 'text-green-700' },
            { label: 'Error', count: servers.filter((s) => s.status === 'error').length, color: 'text-red-700' },
            {
              label: 'Tools Discovered',
              count: servers.reduce((sum, s) => sum + (s.discoveredToolsCount ?? 0), 0),
              color: 'text-blue-700',
            },
          ] as { label: string; count: number; color: string }[]
        ).map(({ label, count, color }) => (
          <Card key={label}>
            <CardContent className="pt-4 pb-4">
              <div className={`text-2xl font-bold ${color}`}>{count}</div>
              <div className="text-sm text-gray-500">{label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Server List */}
      {filteredServers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Server className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium">No MCP servers found</p>
            <p className="text-gray-400 text-sm mt-1">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Click "Add MCP Server" to connect your first MCP server'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredServers.map((server) => {
            const syncResult = syncResults[server.id];
            const isSyncing = syncingId === server.id;
            const isSyncExpanded = expandedSyncId === server.id;
            const isToolsExpanded = expandedToolsId === server.id;

            // Prefer tools from the latest sync result, fall back to server.discoveredTools
            const displayTools: McpDiscoveredTool[] =
              syncResult?.tools ?? server.discoveredTools ?? [];
            const hasTools = displayTools.length > 0 || (server.discoveredToolsCount ?? 0) > 0;

            return (
              <Card key={server.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Info */}
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Server className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900">{server.name}</h3>
                            {statusBadge(server.status)}
                            {!server.isActive && (
                              <Badge variant="outline" className="text-gray-500">
                                Disabled
                              </Badge>
                            )}
                          </div>
                          {server.description && (
                            <p className="text-sm text-gray-500 mt-0.5">{server.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                            <span className="font-mono bg-gray-100 px-2 py-0.5 rounded truncate max-w-xs">
                              {server.serverUrl}
                            </span>
                            <span className="uppercase font-medium text-blue-600">
                              {server.transportType.replace('_', ' ')}
                            </span>
                            <span>{server.discoveredToolsCount ?? 0} tools</span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSync(server)}
                          disabled={isSyncing}
                          className="flex items-center gap-1.5"
                        >
                          {isSyncing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                          Sync
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(server)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteTargetId(server.id)}
                          disabled={deletingId === server.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {deletingId === server.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Status details row */}
                    <div className="flex items-center gap-6 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                      <span>
                        Last connected: {formatDate(server.lastConnectedAt ?? undefined)}
                      </span>
                      {server.lastError && (
                        <span className="text-red-600 flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" />
                          {server.lastError}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Sync Result Panel */}
                  {syncResult && (
                    <div className="border-t border-gray-100">
                      <button
                        onClick={() => setExpandedSyncId(isSyncExpanded ? null : server.id)}
                        className="w-full flex items-center justify-between px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-medium">
                          Last sync — {syncResult.toolsDiscovered} tool(s) discovered
                          {syncResult.errors.length > 0 && (
                            <span className="text-red-600 ml-2">
                              {syncResult.errors.length} error(s)
                            </span>
                          )}
                        </span>
                        {isSyncExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {isSyncExpanded && (
                        <div className="px-5 pb-4 space-y-3 text-xs">
                          <div className="flex items-center gap-3">
                            <div className="bg-blue-50 rounded px-3 py-2 flex-1 text-center">
                              <div className="font-semibold text-blue-800 text-base">
                                {syncResult.toolsDiscovered}
                              </div>
                              <div className="text-blue-600">Discovered</div>
                            </div>
                          </div>
                          {syncResult.errors.length > 0 && (
                            <div className="bg-red-50 rounded p-2 space-y-1">
                              <div className="font-medium text-red-700">Errors:</div>
                              {syncResult.errors.map((e, i) => (
                                <div key={i} className="text-red-600">
                                  {e}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Discovered Tools Panel */}
                  {hasTools && (
                    <div className="border-t border-gray-100">
                      <button
                        onClick={() => setExpandedToolsId(isToolsExpanded ? null : server.id)}
                        className="w-full flex items-center justify-between px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-medium flex items-center gap-1.5">
                          <Wrench className="w-3.5 h-3.5 text-gray-400" />
                          Discovered Tools ({displayTools.length > 0 ? displayTools.length : server.discoveredToolsCount})
                        </span>
                        {isToolsExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {isToolsExpanded && (
                        <div className="px-5 pb-4">
                          {displayTools.length > 0 ? (
                            <DiscoveredToolsList
                              tools={displayTools}
                              onOAuthClick={setOauthDialogTool}
                            />
                          ) : (
                            <p className="text-xs text-gray-400 italic">
                              Tool details not loaded. Run a sync to refresh.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingServer ? 'Edit MCP Server' : 'Add MCP Server'}
            </DialogTitle>
            <DialogDescription>
              {editingServer
                ? 'Update the MCP server configuration.'
                : 'Connect a new Model Context Protocol server to discover and use its tools.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="mcp-name">Name *</Label>
              <Input
                id="mcp-name"
                placeholder="e.g. My MCP Server"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label htmlFor="mcp-desc">Description</Label>
              <Textarea
                id="mcp-desc"
                placeholder="Optional description..."
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Server URL */}
            <div className="space-y-1.5">
              <Label htmlFor="mcp-url">Server URL *</Label>
              <Input
                id="mcp-url"
                type="url"
                placeholder="https://mcp.example.com/sse"
                value={formData.serverUrl}
                onChange={(e) => setFormData((p) => ({ ...p, serverUrl: e.target.value }))}
              />
            </div>

            {/* Transport Type */}
            <div className="space-y-1.5">
              <Label>Transport Type *</Label>
              <Select
                value={formData.transportType}
                onValueChange={(val) =>
                  setFormData((p) => ({ ...p, transportType: val as McpTransportType }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sse">SSE (Server-Sent Events)</SelectItem>
                  <SelectItem value="streamable_http">Streamable HTTP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active */}
            <div className="flex items-center gap-3 pt-1">
              <Switch
                id="mcp-active"
                checked={formData.isActive}
                onCheckedChange={(val) => setFormData((p) => ({ ...p, isActive: val }))}
              />
              <Label htmlFor="mcp-active" className="cursor-pointer">
                Active
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingServer ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* OAuth Assignment Dialog */}
      {oauthDialogTool && (
        <OAuthAssignmentDialog
          tool={oauthDialogTool}
          open={oauthDialogTool !== null}
          onClose={() => setOauthDialogTool(null)}
          oauthStatusMap={oauthStatusMap}
          onStatusChange={handleOAuthStatusChange}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => { if (!open) setDeleteTargetId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete MCP Server</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this MCP server? This action cannot be undone and
              will remove all associated tool discoveries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
